/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FormikProps } from 'formik';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { FormControl, FormHelperText, InputLabel, OutlinedInput, Typography } from '@mui/material';

import { CountryCode, CountryCodes } from 'shared';

import { ArrowRoundedIcon } from '../../../../../components/svg-icons/ArrowRoundedIcon';
import { HUAutocomplete } from '../../../../../elements/HUAutocomplete/HUAutocomplete';
import { destructRenderInputParams } from '../../../../../elements/HUTextInput/utils';
import { Namespaces } from '../../../../../i18n';
import { useFormStyles } from '../../../../../styles/form';
import { useFormsStyles } from '../PersonalForm.styles';
import { PersonalFormApiValidationErrors, PersonalFormValues, ResetApiErrorForField } from '../PersonalForm.types';

type AddressFormProps = FormikProps<PersonalFormValues> & {
  apiErrors: PersonalFormApiValidationErrors;
  resetApiError: ResetApiErrorForField;
  isFetching?: boolean;
  errorMessage?: string;
};

export function AddressForm(props: AddressFormProps) {
  const {
    touched,
    errors,
    values,
    apiErrors,
    handleChange,
    handleBlur,
    isFetching,
    setFieldValue,
    setFieldTouched,
    resetApiError,
  } = props;
  const classes = useFormsStyles();
  const formClasses = useFormStyles();
  const { t } = useTranslation(Namespaces.UserProfile);
  const addressTouched = touched.address || {};
  const addressErrors = errors.address || {};
  const addressValues = values.address || {};

  const hasLine1Error = addressTouched.line1 && !!(addressErrors.line1 || apiErrors['address.line1']);
  const hasLine2Error = addressTouched.line2 && !!(addressErrors.line2 || apiErrors['address.line2']);
  const hasLine3Error = addressTouched.line3 && !!(addressErrors.line3 || apiErrors['address.line3']);
  const hasPostalCodeError =
    addressTouched.postalCode && !!(addressErrors.postalCode || apiErrors['address.postalCode']);
  const hasCityError = addressTouched.city && !!(addressErrors.city || apiErrors['address.city']);
  const hasStateError = addressTouched.state && !!(addressErrors.state || apiErrors['address.state']);
  const hasCountryCodeError =
    addressTouched.countryCode && !!(addressErrors.countryCode || apiErrors['address.countryCode']);

  const line1Error = addressErrors.line1 || apiErrors['address.line1'];
  const line2Error = addressErrors.line2 || apiErrors['address.line2'];
  const line3Error = addressErrors.line3 || apiErrors['address.line3'];
  const postalCodeError = addressErrors.postalCode || apiErrors['address.postalCode'];
  const cityError = addressErrors.city || apiErrors['address.city'];
  const stateError = addressErrors.state || apiErrors['address.state'];
  const countryCodeError = addressErrors.countryCode || apiErrors['address.countryCode'];

  const onChange = useCallback(
    (name: keyof PersonalFormApiValidationErrors, event: React.ChangeEvent) => {
      handleChange(event);
      resetApiError(name);
    },
    [handleChange, resetApiError],
  );

  const handleCountryChange = useCallback(
    async (_: unknown, value: CountryCode | null) => {
      if (value) {
        await setFieldValue('address.countryCode', value, true);
        await setFieldTouched('address.countryCode', true);
        resetApiError('address.countryCode');
      }
    },
    [resetApiError, setFieldTouched, setFieldValue],
  );

  const getOptionLabel = useCallback((value: CountryCode) => t(`countries:${value}.name`), [t]);

  return (
    <div className={classes.addressForm}>
      <Typography variant="h4">{t('profile.address_form_title')}</Typography>
      <FormControl className={cx(formClasses.formControl)} error={hasLine1Error} variant="outlined">
        <InputLabel htmlFor="line1">{t('profile.label.address_line_1')}</InputLabel>
        <OutlinedInput
          id="line1"
          name="address.line1"
          value={addressValues.line1}
          onChange={onChange.bind(null, 'address.line1')}
          label={t('profile.label.address_line_1')}
          disabled={isFetching}
          fullWidth
          onBlur={handleBlur}
        />
        {hasLine1Error && <FormHelperText>{t(`profile.validation.address.line1.${line1Error}`)}</FormHelperText>}
      </FormControl>
      <FormControl className={cx(formClasses.formControl)} error={hasLine2Error} variant="outlined">
        <InputLabel htmlFor="line2">{t('profile.label.address_line_2')}</InputLabel>
        <OutlinedInput
          id="line2"
          name="address.line2"
          value={addressValues.line2 || ''}
          onChange={onChange.bind(null, 'address.line2')}
          label={t('profile.label.address_line_2')}
          disabled={isFetching}
          fullWidth
          onBlur={handleBlur}
        />
        {hasLine2Error && <FormHelperText>{t(`profile.validation.address.line2.${line2Error}`)}</FormHelperText>}
      </FormControl>
      <FormControl className={cx(formClasses.formControl)} error={hasLine3Error} variant="outlined">
        <InputLabel htmlFor="line3">{t('profile.label.address_line_3')}</InputLabel>
        <OutlinedInput
          id="line3"
          name="address.line3"
          value={addressValues.line3 || ''}
          onChange={onChange.bind(null, 'address.line3')}
          label={t('profile.label.address_line_3')}
          disabled={isFetching}
          fullWidth
          onBlur={handleBlur}
        />
        {hasLine3Error && <FormHelperText>{t(`profile.validation.address.line3.${line3Error}`)}</FormHelperText>}
      </FormControl>
      <div className={classes.twoEqualFields}>
        <FormControl
          className={cx(formClasses.formControl, formClasses.firstKeepOffset)}
          error={hasPostalCodeError}
          variant="outlined"
        >
          <InputLabel htmlFor="postalCode">{t('profile.label.postal_code')}</InputLabel>
          <OutlinedInput
            id="postalCode"
            name="address.postalCode"
            value={addressValues.postalCode}
            onChange={onChange.bind(null, 'address.postalCode')}
            label={t('profile.label.postal_code')}
            disabled={isFetching}
            fullWidth
            onBlur={handleBlur}
          />
          {hasPostalCodeError && (
            <FormHelperText>{t(`profile.validation.address.postalCode.${postalCodeError}`)}</FormHelperText>
          )}
        </FormControl>
        <FormControl className={cx(formClasses.formControl)} error={hasCityError} variant="outlined">
          <InputLabel htmlFor="city">{t('profile.label.city')}</InputLabel>
          <OutlinedInput
            id="city"
            name="address.city"
            value={addressValues.city}
            onChange={onChange.bind(null, 'address.city')}
            label={t('profile.label.city')}
            disabled={isFetching}
            fullWidth
            onBlur={handleBlur}
          />
          {hasCityError && <FormHelperText>{t(`profile.validation.address.city.${cityError}`)}</FormHelperText>}
        </FormControl>
      </div>
      <FormControl className={cx(formClasses.formControl)} error={hasStateError} variant="outlined">
        <InputLabel htmlFor="state">{t('profile.label.state')}</InputLabel>
        <OutlinedInput
          id="state"
          name="address.state"
          value={addressValues.state}
          onChange={onChange.bind(null, 'address.state')}
          label={t('profile.label.state')}
          disabled={isFetching}
          fullWidth
          onBlur={handleBlur}
        />
        {hasStateError && <FormHelperText>{t(`profile.validation.address.state.${stateError}`)}</FormHelperText>}
      </FormControl>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthDefault)}
        error={hasCountryCodeError}
        variant="outlined"
      >
        <InputLabel htmlFor="address.countryCode">{t('profile.label.country')}</InputLabel>
        <HUAutocomplete
          renderInput={renderParams => (
            <OutlinedInput
              {...destructRenderInputParams(renderParams)}
              name="address.countryCode"
              label={t('profile.label.country')}
            />
          )}
          id="address.countryCode"
          disableClearable={true}
          options={[...CountryCodes]}
          value={addressValues.countryCode}
          getOptionLabel={getOptionLabel}
          openOnFocus={true}
          onChange={handleCountryChange}
          popupIcon={<ArrowRoundedIcon />}
        />
        {hasCountryCodeError && (
          <FormHelperText>{t(`profile.validation.address.countryCode.${countryCodeError}`)}</FormHelperText>
        )}
      </FormControl>
    </div>
  );
}
